import { Box, Card, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import Swal from "sweetalert2";

import { wrapper } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";

import CampaignEdit from "pages/components/Campaigns/CampaignEdit";
import api from "services/api/campaign";
import sitesAPI from "services/api/campaign";
import gtfsAPI from "services/api/gtfs";

import { formatCampaignDataForSubmit, formatCampaignDataForForm } from "utils/campaigns";
import SoftBox from "components/SoftComponents/SoftBox";
import { loadingScreen } from "utils/wrapper";

function CampaignView({ map, user }) {
    disableMap(map, false);
    api.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0())

    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // 1. Get ID and determine if we are in "Create" mode
    const match = useMatch('campaign/:id');
    const id = match?.params?.id;
    const isNew = id === 'new';

    // Grab the typeId from the URL (e.g., ?typeId=64a1b2c3...)
    const [searchParams] = useSearchParams();
    const typeIdFromUrl = searchParams.get('typeId');

    // 2. Fetch Campaign (or return a blank template if 'new')
    const campaign = useQuery({
        queryKey: ['campaigns', id, typeIdFromUrl],
        queryFn: async () => {
            if (isNew) {
                // Return a blank template with the correct campaignTypeId AND new media fields
                return formatCampaignDataForForm({
                    name: "",
                    campaignTypeId: typeIdFromUrl || "",
                    enabled: true,
                    deviceFilter: [],
                    scheduleType: "alwaysOn",
                    scheduling: {},
                    mediaType: "media",
                    contentId: null,
                    formData: null
                });
            }

            try {
                let response = await api.getCampaign(id);
                return formatCampaignDataForForm(response);
            } catch (e) {
                console.error("Failed to fetch campaign:", e);
                throw e;
            }
        },
        staleTime: isNew ? 0 : (5 * 60 * 1000),
    });

    const tags = useQuery({
        queryKey: ['getAssignedTags'],
        queryFn: async () => {
            return await gtfsAPI.getAssignedTags();
        }
    });

    // 3. Fetch Types
    const campaignTypes = useQuery({
        queryKey: ['campaignTypes'],
        queryFn: async () => api.getCampaignTypes(),
        staleTime: 5 * 60 * 1000,
    });

    // 4. Branch the Save Logic
    const saveForm = async (data) => {
        try {
            const formattedData = formatCampaignDataForSubmit(data);

            if (isNew) {
                const newCampaign = await api.createCampaign(formattedData);
                Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Success</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Campaign created successfully</div>`, "success");
                queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                navigate(`/campaign/${newCampaign._id}`, { replace: true });
            } else {
                await api.saveCampaign(formattedData._id, formattedData);
                Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Success</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Campaign updated successfully</div>`, "success");
                queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
            }
        }
        catch (error) {
            console.error("Save error:", error);
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Failed to save the campaign.</div>`, "error");
        }
    };

    if (campaign.isLoading || campaignTypes.isLoading || tags.isLoading) {
        return loadingScreen(deviceLayout, true);
    }

    if (campaign.isError || !campaign.data || !campaignTypes.data || !tags.data) {
        console.log(campaign.isError, !campaign.data, !campaignTypes.data, !tags.data)
        return wrapper(<Box p={3}>Error loading campaign data.</Box>);
    }

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette: { white }, functions: { rgba } }) => ({ backgroundColor: rgba(white.main, 0.6), overflowY: 'hidden' })}
        >
            <CampaignEdit
                campaign={campaign.data}
                campaignTypes={campaignTypes.data}
                tags={tags.data}
                saveForm={saveForm}
            />

        </SoftBox>
    ), deviceLayout);
}

CampaignView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
}

export default CampaignView;